<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Disponibilidade dos Agentes',
  },
});

const online = computed(() => props.data?.online || 0);
const busy = computed(() => props.data?.busy || 0);
const offline = computed(() => props.data?.offline || 0);
const total = computed(() => online.value + busy.value + offline.value);
const availableRate = computed(() => {
  if (total.value === 0) return 0;
  return ((online.value / total.value) * 100).toFixed(1);
});

const availabilityByHour = computed(() => props.data?.by_hour || []);

function getStatusPercentage(count) {
  if (total.value === 0) return 0;
  return ((count / total.value) * 100).toFixed(1);
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-radio text-lg text-n-teal-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Disponibilidade:</span>
        <span
          class="text-lg font-bold"
          :class="
            availableRate >= 70
              ? 'text-n-teal-11'
              : availableRate >= 50
                ? 'text-n-amber-11'
                : 'text-n-ruby-11'
          "
        >
          {{ availableRate }}%
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-40">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Status circles -->
      <div class="grid grid-cols-3 gap-4">
        <div class="flex flex-col items-center gap-2">
          <div class="relative w-20 h-20">
            <svg class="w-20 h-20 transform -rotate-90">
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-alpha-2"
              />
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-teal-9"
                :stroke-dasharray="`${getStatusPercentage(online) * 2.136} 213.6`"
              />
            </svg>
            <div
              class="absolute inset-0 flex flex-col items-center justify-center"
            >
              <span class="text-xl font-bold text-n-teal-11">{{ online }}</span>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-n-teal-9" />
            <span class="text-xs text-n-slate-11">Online</span>
          </div>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="relative w-20 h-20">
            <svg class="w-20 h-20 transform -rotate-90">
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-alpha-2"
              />
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-amber-9"
                :stroke-dasharray="`${getStatusPercentage(busy) * 2.136} 213.6`"
              />
            </svg>
            <div
              class="absolute inset-0 flex flex-col items-center justify-center"
            >
              <span class="text-xl font-bold text-n-amber-11">{{ busy }}</span>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-n-amber-9" />
            <span class="text-xs text-n-slate-11">Ocupado</span>
          </div>
        </div>

        <div class="flex flex-col items-center gap-2">
          <div class="relative w-20 h-20">
            <svg class="w-20 h-20 transform -rotate-90">
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-alpha-2"
              />
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke-width="8"
                fill="none"
                class="stroke-n-slate-7"
                :stroke-dasharray="`${getStatusPercentage(offline) * 2.136} 213.6`"
              />
            </svg>
            <div
              class="absolute inset-0 flex flex-col items-center justify-center"
            >
              <span class="text-xl font-bold text-n-slate-11">{{
                offline
              }}</span>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-2 h-2 rounded-full bg-n-slate-7" />
            <span class="text-xs text-n-slate-11">Offline</span>
          </div>
        </div>
      </div>

      <!-- Status bar -->
      <div class="flex h-3 rounded-full overflow-hidden bg-n-alpha-1">
        <div
          class="bg-n-teal-9 transition-all duration-300"
          :style="{ width: `${getStatusPercentage(online)}%` }"
        />
        <div
          class="bg-n-amber-9 transition-all duration-300"
          :style="{ width: `${getStatusPercentage(busy)}%` }"
        />
        <div
          class="bg-n-slate-7 transition-all duration-300"
          :style="{ width: `${getStatusPercentage(offline)}%` }"
        />
      </div>

      <!-- Summary -->
      <div
        class="flex items-center justify-center gap-4 text-xs text-n-slate-11"
      >
        <span>{{ total }} agentes no total</span>
        <span>•</span>
        <span>{{ online + busy }} ativos agora</span>
      </div>
    </div>
  </div>
</template>
